import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  MapPin, 
  Calendar, 
  Users, 
  Zap, 
  Shield, 
  TrendingUp,
  Eye,
  ExternalLink,
  Play,
  CheckCircle,
  Star,
  Globe,
  Award
} from 'lucide-react';
import { mockData } from './mock';

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredProjects = mockData.projects?.filter(project => 
    filter === 'all' || project.category === filter
  ) || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30 relative overflow-hidden">
          {/* Subtle background circles for consistency */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-16 right-24 w-80 h-80 bg-blue-200/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 left-16 w-96 h-96 bg-purple-200/10 rounded-full blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-5xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-6 py-3 rounded-full mb-6 border border-blue-200/50">
                <Globe size={16} className="text-blue-600" />
                <span className="text-blue-700 text-sm font-semibold">Our Portfolio</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Transforming Cities Through
                <span 
                  className="block bg-clip-text text-transparent"
                  style={{ 
                    backgroundImage: 'linear-gradient(to right, rgb(6 47 136), rgb(147 51 234))' 
                  }}
                >
                  Smart Technology
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed">
                Explore our successful implementations across Oman and the region, showcasing innovative solutions that make cities smarter, safer, and more sustainable.
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-blue-500" />
                  25+ Cities
                </div>
                <div className="flex items-center gap-2">
                  <Award size={16} className="text-green-500" />
                  500+ Projects
                </div>
                <div className="flex items-center gap-2">
                  <Star size={16} className="text-purple-500" />
                  99% Success Rate
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              className="rounded-full"
            >
              All Projects
            </Button>
            <Button
              variant={filter === 'lighting' ? 'default' : 'outline'}
              onClick={() => setFilter('lighting')}
              className="rounded-full"
            >
              Smart Lighting
            </Button>
            <Button
              variant={filter === 'traffic' ? 'default' : 'outline'}
              onClick={() => setFilter('traffic')}
              className="rounded-full"
            >
              Traffic Systems
            </Button>
            <Button
              variant={filter === 'monitoring' ? 'default' : 'outline'}
              onClick={() => setFilter('monitoring')}
              className="rounded-full"
            >
              City Monitoring
            </Button>
            <Button
              variant={filter === 'solar' ? 'default' : 'outline'}
              onClick={() => setFilter('solar')}
              className="rounded-full"
            >
              Solar Solutions
            </Button>
            <Button
              variant={filter === 'safety' ? 'default' : 'outline'}
              onClick={() => setFilter('safety')}
              className="rounded-full"
            >
              Safety Systems
            </Button>
            <Button
              variant={filter === 'charging' ? 'default' : 'outline'}
              onClick={() => setFilter('charging')}
              className="rounded-full"
            >
              EV Charging
            </Button>
            <Button
              variant={filter === 'energy' ? 'default' : 'outline'}
              onClick={() => setFilter('energy')}
              className="rounded-full"
            >
              Energy Storage
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Featured Projects
            </h2>
            <p className="text-xl text-gray-600">
              Discover our most impactful implementations across the region.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => {
              return (
                <Card 
                  key={project.id} 
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 group flex flex-col h-full"
                >
                  <div className="relative">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-white/90 text-gray-900 hover:bg-white">
                        {project.category}
                      </Badge>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
                    </div>
                  </div>
                  
                  <CardContent className="p-6 flex flex-col flex-grow">
                    <div className="flex-grow">
                      <p className="text-gray-600 mb-4">{project.description}</p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.technologies.map((tech, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {tech}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div className="mt-auto">
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                          if (project.id === 2) {
                            setSelectedProject(project);
                          } else {
                            navigate(`/projects/${project.id}`);
                          }
                        }}
                      >
                        <Eye size={16} className="mr-2" />
                        View Case Study
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project Statistics */}
      {/* <section className="py-16 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Project Impact
            </h2>
            <p className="text-xl text-gray-600">
              Numbers that speak to our success and commitment to excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Zap size={32} className="text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">70%</div>
                <p className="text-gray-600">Average Energy Savings</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <TrendingUp size={32} className="text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">500+</div>
                <p className="text-gray-600">Projects Completed</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Shield size={32} className="text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">99%</div>
                <p className="text-gray-600">Client Satisfaction</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Globe size={32} className="text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">25+</div>
                <p className="text-gray-600">Cities Served</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section> */}

      {/* Project Detail Modal for Project ID 2 */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="relative pt-8">
              <img 
                src={selectedProject.image} 
                alt={selectedProject.title}
                className="w-full h-64 object-cover rounded-t-2xl"
              />
              <Button 
                variant="ghost" 
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-600"
              >
                ×
              </Button>
            </div>

            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedProject.title}</h2>
                  <div className="flex items-center gap-4 text-gray-600">
                    {selectedProject.location && (
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-blue-500" />
                        <span>{selectedProject.location}</span>
                      </div>
                    )}
                    {selectedProject.duration && (
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-green-500" />
                        <span>{selectedProject.duration}</span>
                      </div>
                    )}
                    {selectedProject.client && (
                      <div className="flex items-center gap-2">
                        <Users size={16} className="text-purple-500" />
                        <span>{selectedProject.client}</span>
                      </div>
                    )}
                  </div>
                </div>
                <Badge className="bg-blue-100 text-blue-700">
                  {selectedProject.category}
                </Badge>
              </div>

              <div className="prose max-w-none mb-8">
                <p className="text-lg text-gray-600 mb-6">{selectedProject.description}</p>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Project Overview</h3>
                <p className="text-gray-600 mb-6">{selectedProject.overview}</p>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Features</h3>
                <ul className="grid md:grid-cols-2 gap-2 mb-6">
                  {selectedProject.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-gray-600">
                      <CheckCircle size={16} className="text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Technologies Used</h3>
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedProject.technologies.map((tech, index) => (
                    <Badge key={index} variant="outline">
                      {tech}
                    </Badge>
                  ))}
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Results & Impact</h3>
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{selectedProject.results.energySaved}</div>
                    <div className="text-sm text-gray-600">Energy Savings</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{selectedProject.results.costReduction}</div>
                    <div className="text-sm text-gray-600">Cost Reduction</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{selectedProject.results.improvement}</div>
                    <div className="text-sm text-gray-600">Efficiency Improvement</div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedProject(null)}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={() => navigate('/contact')}
                >
                  <ExternalLink size={16} className="mr-2" />
                  Start Your Project
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client Testimonials */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied clients across the region.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.projects?.slice(0, 3).map((project) => (
              <Card key={project.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">
                    "{project.testimonial}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                      T
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Client Testimonial</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Your Smart City Project?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help transform your city with innovative technology solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
              <ExternalLink size={20} className="mr-2" />
              Start Your Project
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
              <Play size={20} className="mr-2" />
              Watch Demo
            </Button>
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
};
